/**
 * ImagePicker - A reusable modal component for selecting images from the Media Library.
 * Supports single and multi-select modes, filtering by folder, tags, search.
 * Shows smart crop preview when a target aspect ratio is specified.
 * Optionally includes image type selector for dynamic crop preview.
 */
import React, { useState, useCallback, useMemo, useEffect } from 'react';
import {
  XMarkIcon,
  MagnifyingGlassIcon,
  FolderIcon,
  ArrowUpTrayIcon,
  CheckIcon,
  PhotoIcon,
  ScissorsIcon,
} from '@heroicons/react/24/outline';
import { Button } from '@/components/ui';
import {
  useMediaFolders,
  useMediaTags,
  useMediaImages,
} from '@/hooks/queries/useMediaQueries';
import { UploadModal } from '@/pages/MediaLibrary/components/UploadModal';
import type { ImageCategory, MediaImage, MediaTag } from '@/types/media';
import { formatFileSize, getAspectRatioLabel, getDisplayUrl } from '@/types/media';
import {
  isTargetedCompatible,
  type TargetedFilterCriteria,
} from './targetedCompatibility';

// =============================================================================
// Types
// =============================================================================

export type AspectRatioFilter = '1:1' | '16:9' | '4:5' | '1.91:1' | '4:1';

export interface ImageTypeOption {
  value: string;
  label: string;
  description: string;
  aspectRatio: string;
}

export interface ImagePickerProps {
  isOpen: boolean;
  onClose: () => void;
  selectedImageIds: string[];
  /**
   * Called when the user confirms a selection.
   *
   * - `imageIds`: the selected image IDs.
   * - `selectedImageType`: only set when the picker was rendered with
   *   `imageTypeOptions`.
   * - `images`: the resolved `MediaImage` objects for those IDs, taken
   *   directly from the picker's own list. Orchestrating components (e.g.
   *   `TargetedImagePicker`) should prefer this over re-resolving IDs against
   *   a separately-fetched list — that approach silently drops selections
   *   when the picker's filters (folder/tags/search) don't match.
   */
  onConfirm: (
    imageIds: string[],
    selectedImageType?: string,
    images?: MediaImage[]
  ) => void;
  maxSelection?: number;
  /** Target aspect ratio - images will be cropped to this ratio if they don't match */
  targetAspectRatio?: AspectRatioFilter;
  /** @deprecated Use targetAspectRatio instead */
  aspectRatioFilter?: AspectRatioFilter;
  /** Image type options for the selector. If provided, shows a type selector in the picker. */
  imageTypeOptions?: ImageTypeOption[];
  /** Initial image type when using imageTypeOptions */
  initialImageType?: string;
  initialFolderId?: string;
  initialTagIds?: string[];
  /**
   * Restrict the source list to a specific category. Used by the platform-aware
   * `TargetedImagePicker` flow to enforce that only Approved images can be picked
   * for ad-creation cropping. Omit to show Reference + Approved (Targeted is
   * always excluded server-side).
   *
   * Ignored when `categoryTabs` is provided — the active tab wins.
   */
  categoryFilter?: ImageCategory;
  /**
   * Render a category-tab strip below the header. When provided, the active
   * tab's `value` is used as the `category` filter on the underlying images
   * query, superseding `categoryFilter`. The first item is the default tab.
   *
   * Used by `TargetedImagePicker` to surface both `'approved'` (pick a source
   * to crop) and `'targeted'` (pre-cropped, ready to reuse) in one modal.
   */
  categoryTabs?: { value: ImageCategory; label: string; subtitle?: string }[];
  /**
   * **Strict** server-side filter on `placement_key`. When set AND the
   * active tab is `'targeted'`, only Targeteds with this exact placement_key
   * are returned. Use `targetedCompatibility` instead when you want
   * cross-placement reuse (e.g. surfacing same-aspect-ratio crops from
   * other placements / platforms).
   *
   * Reference/Approved rows carry `placement_key = NULL`, so this filter
   * applied alongside `category != 'targeted'` would return zero rows;
   * it's only forwarded to the backend on the Targeted tab.
   */
  placementKeyFilter?: string;
  /**
   * Server-side compatibility filter for the Targeted tab. The backend
   * narrows Targeted listings to rows whose origin spec matches by aspect
   * ratio, whose parent `crop_box` is large enough, and whose filesize fits
   * the active placement's cap — see `MediaCRUD.get_images`'s
   * `compatible_with_placement_key` arg. Replaces the page-size-bounded
   * client filter for the same intent. Mutually exclusive in spirit with
   * `placementKeyFilter` — when both are set, compatibility wins (server
   * drops strict-key filter; compat is a strict superset that includes the
   * exact-key match).
   */
  compatibleWithPlacementKey?: string;
  /**
   * Defensive client-side compatibility filter (post-fetch). Set this when
   * you want a backstop that catches rows the server-side filter would
   * miss (registry drift, schema migration in flight). Normally a no-op
   * because `compatibleWithPlacementKey` already narrows server-side.
   *
   * See `./targetedCompatibility.ts` for the full check semantics.
   */
  targetedCompatibility?: TargetedFilterCriteria;
  /**
   * When false, the picker does NOT call `onClose` after the user confirms a
   * selection — the parent decides when to close. Required by orchestrating
   * components (e.g. `TargetedImagePicker`) that need the picker to stay
   * mounted while a follow-up step (crop modal) runs. Default true preserves
   * existing single-step picker behavior for legacy callers.
   */
  closeOnConfirm?: boolean;
}

interface ImagePickerCardProps {
  image: MediaImage;
  isSelected: boolean;
  onToggle: () => void;
  disabled?: boolean;
  targetAspectRatio?: AspectRatioFilter;
}

const TARGETED_BADGE_LABEL = 'Reusable crop';

// =============================================================================
// Constants
// =============================================================================

// Tolerance for aspect ratio matching
const ASPECT_RATIO_TOLERANCE = 0.08;

// Target ratios as numbers
const TARGET_RATIOS: Record<string, number> = {
  '1:1': 1,
  '16:9': 16 / 9,
  '4:5': 4 / 5,
  '1.91:1': 1.91,
  '4:1': 4,
};

// =============================================================================
// Helper Functions
// =============================================================================

function parseAspectRatio(ratioStr: string): number {
  // Handle formats like "1.91:1", "4:5", "1:1"
  if (ratioStr.includes(':')) {
    const [w, h] = ratioStr.split(':').map(Number);
    return w / h;
  }
  return parseFloat(ratioStr) || 1;
}

function matchesAspectRatio(
  width: number,
  height: number,
  targetRatioStr: string
): boolean {
  const ratio = width / height;
  const targetRatio = TARGET_RATIOS[targetRatioStr] || parseAspectRatio(targetRatioStr);
  return Math.abs(ratio - targetRatio) <= ASPECT_RATIO_TOLERANCE;
}

/**
 * Calculate the crop area for smart cropping (center crop).
 * Returns CSS object-position and object-fit values, plus crop dimensions.
 */
function calculateCropPreview(
  imageWidth: number,
  imageHeight: number,
  targetRatioStr: string
): { needsCrop: boolean; cropStyle: React.CSSProperties; cropPercent: number } {
  const currentRatio = imageWidth / imageHeight;
  const target = TARGET_RATIOS[targetRatioStr] || parseAspectRatio(targetRatioStr);

  // Check if already matches
  if (Math.abs(currentRatio - target) <= ASPECT_RATIO_TOLERANCE) {
    return {
      needsCrop: false,
      cropStyle: { objectFit: 'contain' as const },
      cropPercent: 0,
    };
  }

  // Calculate how much will be cropped
  let cropPercent: number;
  if (currentRatio > target) {
    // Image is wider than target - crop sides
    const newWidth = imageHeight * target;
    cropPercent = Math.round(((imageWidth - newWidth) / imageWidth) * 100);
  } else {
    // Image is taller than target - crop top/bottom
    const newHeight = imageWidth / target;
    cropPercent = Math.round(((imageHeight - newHeight) / imageHeight) * 100);
  }

  return {
    needsCrop: true,
    cropStyle: {
      objectFit: 'cover' as const,
      objectPosition: 'center',
    },
    cropPercent,
  };
}

// =============================================================================
// Crop Preview Overlay Component
// =============================================================================

interface CropPreviewProps {
  image: MediaImage;
  targetRatio: string;
}

function CropPreviewOverlay({ image, targetRatio }: CropPreviewProps) {
  const { needsCrop, cropPercent } = calculateCropPreview(
    image.width,
    image.height,
    targetRatio
  );

  if (!needsCrop) return null;

  return (
    <div className="absolute top-2 right-2 z-10 flex items-center gap-1 bg-amber-500 text-white text-[10px] px-1.5 py-0.5 rounded shadow">
      <ScissorsIcon className="w-3 h-3" />
      <span>Crop {cropPercent}%</span>
    </div>
  );
}

// =============================================================================
// Image Card Component with Crop Preview
// =============================================================================

function ImagePickerCard({
  image,
  isSelected,
  onToggle,
  disabled,
  targetAspectRatio,
}: ImagePickerCardProps) {
  // Targeted rows are already cropped/encoded outputs for some placement.
  // On the Targeted tab they may originate from a different placement (or
  // platform) that shares the active aspect ratio — surfaced via the
  // `compatible_with_placement_key` server filter — but they're still
  // pre-cropped bytes, not a source needing a crop. Don't compute or
  // surface a crop overlay for them; it would suggest a re-crop is needed
  // when in fact the bytes already fit.
  const isTargeted = image.category === 'targeted';

  // Calculate crop preview if target ratio is set
  const cropInfo = useMemo(() => {
    if (!targetAspectRatio || isTargeted) return null;
    return calculateCropPreview(image.width, image.height, targetAspectRatio);
  }, [image.width, image.height, targetAspectRatio, isTargeted]);

  // Determine the aspect ratio for the preview container based on target
  const containerAspectRatio = targetAspectRatio
    ? (TARGET_RATIOS[targetAspectRatio] || parseAspectRatio(targetAspectRatio))
    : 1;

  return (
    <div
      onClick={disabled && !isSelected ? undefined : onToggle}
      className={`
        relative bg-white rounded-lg border overflow-hidden
        transition-all duration-150
        ${disabled && !isSelected
          ? 'opacity-50 cursor-not-allowed'
          : 'cursor-pointer hover:shadow-md'
        }
        ${isSelected
          ? 'ring-2 ring-blue-500 border-blue-500'
          : 'border-gray-200 hover:border-gray-300'
        }
      `}
    >
      {/* Selection indicator */}
      <div
        className={`
          absolute top-2 left-2 z-10 w-5 h-5 rounded-full border-2 flex items-center justify-center
          transition-all
          ${isSelected
            ? 'bg-blue-500 border-blue-500'
            : 'bg-white/80 border-gray-300'
          }
        `}
      >
        {isSelected && <CheckIcon className="w-3 h-3 text-white" />}
      </div>

      {/* Crop indicator badge — only for Approved/Reference rows that may need cropping. */}
      {targetAspectRatio && !isTargeted && (
        <CropPreviewOverlay image={image} targetRatio={targetAspectRatio} />
      )}

      {/* Targeted rows wear a "Reusable crop" badge so users can tell them
          apart from Approved rows in the merged tab listing. */}
      {isTargeted && (
        <div
          className="absolute top-2 right-2 z-10 flex items-center gap-1 bg-emerald-600 text-white text-[10px] px-1.5 py-0.5 rounded shadow"
          aria-label={TARGETED_BADGE_LABEL}
        >
          <CheckIcon className="w-3 h-3" />
          <span>{TARGETED_BADGE_LABEL}</span>
        </div>
      )}

      {/* Image preview - shows cropped preview if target ratio is set */}
      <div
        className="bg-gray-100 overflow-hidden transition-all duration-200"
        style={{
          aspectRatio: targetAspectRatio ? containerAspectRatio : 1,
        }}
      >
        <img
          src={getDisplayUrl(image)}
          alt={image.alt_text || image.original_filename}
          className="w-full h-full transition-all duration-200"
          style={cropInfo?.needsCrop ? cropInfo.cropStyle : { objectFit: 'contain' }}
          loading="lazy"
        />
      </div>

      {/* Info */}
      <div className="p-2">
        <p className="text-xs font-medium text-gray-900 truncate" title={image.original_filename}>
          {image.original_filename}
        </p>
        <div className="flex items-center justify-between mt-1">
          <span className="text-xs text-gray-500">{formatFileSize(image.file_size_bytes)}</span>
          <span className="text-xs text-gray-400">
            {image.width}x{image.height}
          </span>
        </div>
        <p className="text-xs text-gray-400 mt-1">
          {getAspectRatioLabel(image.width, image.height)}
          {cropInfo?.needsCrop && targetAspectRatio && (
            <span className="text-amber-600 ml-1">→ {targetAspectRatio}</span>
          )}
        </p>
      </div>
    </div>
  );
}

// =============================================================================
// Selected Images Bar Component with Crop Preview
// =============================================================================

interface SelectedImagesBarProps {
  selectedImages: MediaImage[];
  onRemove: (id: string) => void;
  targetAspectRatio?: string;
}

function SelectedImagesBar({ selectedImages, onRemove, targetAspectRatio }: SelectedImagesBarProps) {
  if (selectedImages.length === 0) return null;

  const containerAspectRatio = targetAspectRatio
    ? (TARGET_RATIOS[targetAspectRatio] || parseAspectRatio(targetAspectRatio))
    : 1;

  return (
    <div className="border-t border-gray-200 p-3 bg-gray-50">
      <div className="flex items-center gap-2 mb-2">
        <span className="text-sm font-medium text-gray-700">
          Selected ({selectedImages.length})
        </span>
        {targetAspectRatio && (
          <span className="text-xs text-gray-500">
            • Will be cropped to {targetAspectRatio}
          </span>
        )}
      </div>
      <div className="flex gap-2 overflow-x-auto pb-2">
        {selectedImages.map((image) => {
          const cropInfo = targetAspectRatio
            ? calculateCropPreview(image.width, image.height, targetAspectRatio)
            : null;

          return (
            <div
              key={image.id}
              className="relative flex-shrink-0 rounded border border-gray-200 overflow-hidden bg-gray-100 transition-all duration-200"
              style={{
                width: targetAspectRatio ? (containerAspectRatio >= 1 ? 80 : 64) : 64,
                height: targetAspectRatio ? (containerAspectRatio >= 1 ? 80 / containerAspectRatio : 64 / containerAspectRatio) : 64,
              }}
            >
              <img
                src={getDisplayUrl(image)}
                alt={image.alt_text || image.original_filename}
                className="w-full h-full transition-all duration-200"
                style={cropInfo?.needsCrop ? cropInfo.cropStyle : { objectFit: 'contain' }}
              />
              {/* Crop indicator */}
              {cropInfo?.needsCrop && (
                <div className="absolute bottom-0 left-0 right-0 bg-amber-500/90 text-white text-[8px] text-center py-0.5">
                  <ScissorsIcon className="w-2 h-2 inline mr-0.5" />
                  Crop {cropInfo.cropPercent}%
                </div>
              )}
              <button
                onClick={() => onRemove(image.id)}
                className="absolute top-0.5 right-0.5 w-4 h-4 bg-red-500 rounded-full flex items-center justify-center hover:bg-red-600 transition-colors"
              >
                <XMarkIcon className="w-3 h-3 text-white" />
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// =============================================================================
// Tag Filter Chips Component (simplified for picker)
// =============================================================================

interface PickerTagFilterProps {
  tags: MediaTag[];
  selectedTagIds: string[];
  onChange: (tagIds: string[]) => void;
}

function PickerTagFilter({ tags, selectedTagIds, onChange }: PickerTagFilterProps) {
  const toggleTag = (tagId: string) => {
    if (selectedTagIds.includes(tagId)) {
      onChange(selectedTagIds.filter((id) => id !== tagId));
    } else {
      onChange([...selectedTagIds, tagId]);
    }
  };

  if (tags.length === 0) return null;

  return (
    <div className="flex items-center gap-1.5 flex-wrap">
      {tags.slice(0, 10).map((tag) => {
        const isSelected = selectedTagIds.includes(tag.id);
        return (
          <button
            key={tag.id}
            onClick={() => toggleTag(tag.id)}
            className={`
              inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium
              transition-all cursor-pointer
              ${isSelected
                ? 'ring-1 ring-offset-1'
                : 'opacity-60 hover:opacity-100'
              }
            `}
            style={{
              backgroundColor: `${tag.color}20`,
              color: tag.color,
              ...(isSelected ? { ringColor: tag.color } : {}),
            }}
          >
            {tag.name}
          </button>
        );
      })}
      {selectedTagIds.length > 0 && (
        <button
          onClick={() => onChange([])}
          className="text-xs text-gray-500 hover:text-gray-700 ml-1"
        >
          Clear
        </button>
      )}
    </div>
  );
}

// =============================================================================
// Main ImagePicker Component
// =============================================================================

export function ImagePicker({
  isOpen,
  onClose,
  selectedImageIds: initialSelectedIds,
  onConfirm,
  maxSelection,
  targetAspectRatio,
  aspectRatioFilter, // deprecated, use targetAspectRatio
  imageTypeOptions,
  initialImageType,
  initialFolderId,
  initialTagIds = [],
  categoryFilter,
  categoryTabs,
  placementKeyFilter,
  compatibleWithPlacementKey,
  targetedCompatibility,
  closeOnConfirm = true,
}: ImagePickerProps) {
  // Active category-tab state. The default is the first tab item; null when
  // `categoryTabs` is not provided (in which case `categoryFilter` drives the
  // category arg below).
  const [activeTabCategory, setActiveTabCategory] = useState<ImageCategory | null>(
    () => categoryTabs?.[0]?.value ?? null
  );

  // Tabs win over `categoryFilter` when present. This is documented on the
  // prop — keeping `categoryFilter` for legacy single-category callers
  // (upload flow, etc.) that aren't migrated to tabs.
  const effectiveCategory: ImageCategory | undefined =
    categoryTabs && activeTabCategory ? activeTabCategory : categoryFilter;
  // Local state for selections (we only confirm on submit)
  const [localSelectedIds, setLocalSelectedIds] = useState<Set<string>>(
    new Set(initialSelectedIds)
  );

  // Cache of selected MediaImages keyed by id, kept in sync with `localSelectedIds`.
  // Survives filter changes (folder/tag/search), unlike `allImages` which is the
  // current filter slice only. Used when confirming so we can hand callers the
  // resolved MediaImage[] for selections that happened across multiple filters.
  // Also feeds the selected-bar preview so previously-selected items don't
  // disappear when the user changes filters.
  const [selectedImageCache, setSelectedImageCache] = useState<Map<string, MediaImage>>(
    () => new Map()
  );

  // Image type selector state (when imageTypeOptions provided)
  const [selectedImageType, setSelectedImageType] = useState<string>(
    initialImageType || (imageTypeOptions?.[0]?.value ?? '')
  );

  // Get the effective target ratio - either from type selector or props
  const effectiveTargetRatio = useMemo(() => {
    if (imageTypeOptions && selectedImageType) {
      const option = imageTypeOptions.find(opt => opt.value === selectedImageType);
      return option?.aspectRatio as AspectRatioFilter | undefined;
    }
    return targetAspectRatio || aspectRatioFilter;
  }, [imageTypeOptions, selectedImageType, targetAspectRatio, aspectRatioFilter]);

  // Filter state
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedFolderId, setSelectedFolderId] = useState<string | null>(
    initialFolderId || null
  );
  const [selectedTagIds, setSelectedTagIds] = useState<string[]>(initialTagIds);

  // Upload modal state
  const [showUploadModal, setShowUploadModal] = useState(false);

  // Queries
  const { data: folders = [] } = useMediaFolders();
  const { data: tags = [] } = useMediaTags();
  // Server-side `placement_key` filter applies only on the Targeted tab.
  // Reference/Approved rows have `placement_key = NULL`, so applying it
  // anywhere else returns zero rows. When `compatibleWithPlacementKey` is
  // set, the server's compatibility filter (a strict superset of the exact
  // placement_key match) takes over and we drop the strict filter — this
  // is what enables cross-placement reuse without losing compatible rows
  // past the page boundary.
  const isTargetedTab = effectiveCategory === 'targeted';
  const useCompatFilter = isTargetedTab && !!compatibleWithPlacementKey;
  const effectivePlacementKey =
    isTargetedTab && !useCompatFilter ? placementKeyFilter : undefined;
  const effectiveCompatKey = useCompatFilter ? compatibleWithPlacementKey : undefined;

  const { data: imagesResponse, isLoading: imagesLoading } = useMediaImages({
    folder_id: selectedFolderId || undefined,
    tag_ids: selectedTagIds.length > 0 ? selectedTagIds : undefined,
    search: searchQuery || undefined,
    category: effectiveCategory,
    placement_key: effectivePlacementKey,
    compatible_with_placement_key: effectiveCompatKey,
    page: 1,
    page_size: 200, // Load all for now, will optimize with thumbnails
    sort_by: 'created_at',
    sort_order: 'desc',
  });

  // Reset local state when modal opens
  useEffect(() => {
    if (isOpen) {
      setLocalSelectedIds(new Set(initialSelectedIds));
      setSelectedImageCache(new Map());
      setSearchQuery('');
      setSelectedFolderId(initialFolderId || null);
      setSelectedTagIds(initialTagIds);
      setSelectedImageType(initialImageType || (imageTypeOptions?.[0]?.value ?? ''));
      // Reopening the picker should land the user back on the first
      // (default) tab. Without this reset, a second open after the user
      // browsed the Targeted tab on the prior session would surprise them
      // by landing on Targeted again.
      setActiveTabCategory(categoryTabs?.[0]?.value ?? null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // All images returned for the current filter set. When the Targeted tab
  // is in compatibility mode, the server already applied the
  // `compatible_with_placement_key` filter (ratio + parent crop_box pixels
  // + filesize) and only returns rows reusable for the active placement.
  // The client-side `targetedCompatibility` filter here is a defensive
  // backstop — normally a no-op, but it catches drift between the backend
  // spec registry and the frontend `usePlatformSpecs` data (or any future
  // bug in the SQL filter). See `./targetedCompatibility.ts`.
  const allImages = useMemo(() => {
    const raw = imagesResponse?.images || [];
    if (isTargetedTab && targetedCompatibility) {
      return raw.filter((img) => isTargetedCompatible(img, targetedCompatibility));
    }
    return raw;
  }, [imagesResponse?.images, isTargetedTab, targetedCompatibility]);

  // Get full image objects for selected IDs. Pulls from `selectedImageCache`
  // (which survives filter changes), falling back to `allImages` for any id not
  // yet cached (e.g. seeded via `initialSelectedIds`).
  const selectedImages = useMemo(() => {
    return Array.from(localSelectedIds)
      .map((id) => selectedImageCache.get(id) ?? allImages.find((img) => img.id === id))
      .filter((img): img is MediaImage => !!img);
  }, [allImages, localSelectedIds, selectedImageCache]);

  // Count images that need cropping
  const cropStats = useMemo(() => {
    if (!effectiveTargetRatio) return { needsCrop: 0, total: 0 };

    const needsCrop = selectedImages.filter(
      (img) => !matchesAspectRatio(img.width, img.height, effectiveTargetRatio)
    ).length;

    return { needsCrop, total: selectedImages.length };
  }, [selectedImages, effectiveTargetRatio]);

  // Handlers
  const handleToggleImage = useCallback(
    (imageId: string) => {
      // Direction is determined by the *selection state*, not cache membership.
      // The cache may legitimately be out of step with `localSelectedIds` (e.g.
      // when seeded via `initialSelectedIds` before `allImages` resolves), so
      // using `cache.has(imageId)` to decide add-vs-delete inverts the toggle
      // for those rows.
      const wasSelected = localSelectedIds.has(imageId);
      const willBeSelected = !wasSelected;

      // If adding would exceed maxSelection, bail BEFORE touching either
      // setter — otherwise the cache update would still add the row even
      // though `setLocalSelectedIds` returned `prev` (no-op), leaving the
      // two states out of sync.
      if (willBeSelected && maxSelection && localSelectedIds.size >= maxSelection) {
        return;
      }

      setLocalSelectedIds((prev) => {
        const next = new Set(prev);
        if (willBeSelected) {
          next.add(imageId);
        } else {
          next.delete(imageId);
        }
        return next;
      });

      setSelectedImageCache((prev) => {
        const next = new Map(prev);
        if (willBeSelected) {
          const img = allImages.find((m) => m.id === imageId);
          if (img) next.set(imageId, img);
        } else {
          next.delete(imageId);
        }
        return next;
      });
    },
    [localSelectedIds, maxSelection, allImages]
  );

  const handleRemoveSelected = useCallback((imageId: string) => {
    setLocalSelectedIds((prev) => {
      if (!prev.has(imageId)) return prev;
      const next = new Set(prev);
      next.delete(imageId);
      return next;
    });
    setSelectedImageCache((prev) => {
      if (!prev.has(imageId)) return prev;
      const next = new Map(prev);
      next.delete(imageId);
      return next;
    });
  }, []);

  // Seeding: when `allImages` arrives (or changes filters), fill in cache
  // entries for any selected ids that don't yet have a MediaImage cached.
  // Without this, ids passed via `initialSelectedIds` never get resolved into
  // the cache and `handleConfirm` would have to fall back to `allImages`,
  // which loses prior-filter selections.
  useEffect(() => {
    if (allImages.length === 0) return;
    setSelectedImageCache((prev) => {
      let changed = false;
      const next = new Map(prev);
      for (const id of localSelectedIds) {
        if (next.has(id)) continue;
        const img = allImages.find((m) => m.id === id);
        if (img) {
          next.set(id, img);
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [allImages, localSelectedIds]);

  const handleConfirm = useCallback(() => {
    const ids = Array.from(localSelectedIds);
    // Resolve to MediaImage[] from the cache (filter-change-survivable) with
    // a fallback to `allImages` for any id not yet cached. Callers that need
    // strict 1:1 resolution should compare `ids.length === images.length`.
    const images = ids
      .map((id) => selectedImageCache.get(id) ?? allImages.find((img) => img.id === id))
      .filter((img): img is MediaImage => !!img);

    if (imageTypeOptions) {
      onConfirm(ids, selectedImageType, images);
    } else {
      onConfirm(ids, undefined, images);
    }
    if (closeOnConfirm) {
      onClose();
    }
  }, [
    localSelectedIds,
    selectedImageCache,
    allImages,
    selectedImageType,
    imageTypeOptions,
    onConfirm,
    onClose,
    closeOnConfirm,
  ]);

  const handleCancel = useCallback(() => {
    onClose();
  }, [onClose]);

  // Check if at max selection
  const isAtMaxSelection = maxSelection ? localSelectedIds.size >= maxSelection : false;

  // Get current image type option for display
  const currentTypeOption = imageTypeOptions?.find(opt => opt.value === selectedImageType);

  if (!isOpen) return null;

  return (
    <>
      <div className="fixed inset-0 z-50 overflow-hidden">
        <div className="flex min-h-screen items-start justify-center pt-8 px-4 pb-4">
          {/* Backdrop */}
          <div
            className="fixed inset-0 bg-black/50 transition-opacity"
            onClick={handleCancel}
            aria-hidden="true"
          />

          {/* Modal */}
          <div
            className="relative bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[calc(100vh-4rem)] flex flex-col"
            role="dialog"
            aria-modal="true"
            aria-labelledby="image-picker-title"
          >
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <div>
                <h2 id="image-picker-title" className="text-lg font-semibold text-gray-900">
                  Select Images
                  {maxSelection && (
                    <span className="ml-2 text-sm font-normal text-gray-500">
                      ({localSelectedIds.size}/{maxSelection})
                    </span>
                  )}
                </h2>
                {effectiveTargetRatio && !imageTypeOptions && (
                  <p className="text-xs text-gray-500 mt-0.5">
                    Target: {effectiveTargetRatio} ratio • Images will be smart-cropped if needed
                  </p>
                )}
              </div>
              <button
                onClick={handleCancel}
                className="text-gray-400 hover:text-gray-600 transition-colors"
                aria-label="Close"
              >
                <XMarkIcon className="w-5 h-5" />
              </button>
            </div>

            {/* Category tabs (when categoryTabs provided). Surfaces both the
                Approved (pick-to-crop) and Targeted (pre-cropped, ready to
                reuse) categories in one modal — see TargetedImagePicker for
                the orchestration that branches on which tab confirmed. */}
            {categoryTabs && categoryTabs.length > 0 && (
              <div
                className="border-b border-gray-200"
                role="tablist"
                aria-label="Image category"
              >
                <div className="flex px-4 gap-1">
                  {categoryTabs.map((tab) => {
                    const isActive = tab.value === activeTabCategory;
                    return (
                      <button
                        key={tab.value}
                        type="button"
                        role="tab"
                        aria-selected={isActive}
                        onClick={() => setActiveTabCategory(tab.value)}
                        className={`
                          relative px-3 py-2 text-sm font-medium transition-colors
                          border-b-2 -mb-px
                          ${isActive
                            ? 'border-blue-500 text-blue-700'
                            : 'border-transparent text-gray-600 hover:text-gray-900'
                          }
                        `}
                      >
                        {tab.label}
                      </button>
                    );
                  })}
                </div>
                {/* Active-tab subtitle. Drawn from the active tab's
                    `subtitle` so callers can clarify each tab's purpose
                    (e.g. "Pick a source to crop" vs "Pre-cropped, ready to
                    reuse") without renaming the tab labels themselves. */}
                {(() => {
                  const active = categoryTabs.find((t) => t.value === activeTabCategory);
                  return active?.subtitle ? (
                    <p className="px-4 pb-2 text-xs text-gray-500">{active.subtitle}</p>
                  ) : null;
                })()}
              </div>
            )}

            {/* Image Type Selector (when imageTypeOptions provided) */}
            {imageTypeOptions && (
              <div className="px-4 py-3 bg-blue-50 border-b border-blue-100">
                <div className="flex items-center gap-4">
                  <label className="text-sm font-medium text-blue-900 whitespace-nowrap">
                    Image Type:
                  </label>
                  <select
                    value={selectedImageType}
                    onChange={(e) => setSelectedImageType(e.target.value)}
                    className="flex-1 min-w-0 max-w-md pl-3 pr-8 py-1.5 text-sm border border-blue-200 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 truncate"
                  >
                    {imageTypeOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label} ({option.aspectRatio})
                      </option>
                    ))}
                  </select>
                </div>
                {currentTypeOption && (
                  <p className="text-xs text-blue-700 mt-1.5">
                    {currentTypeOption.description} • Images will be smart-cropped to {currentTypeOption.aspectRatio} if needed
                  </p>
                )}
              </div>
            )}

            {/* Filters */}
            <div className="p-4 border-b border-gray-200 space-y-3">
              {/* Row 1: Search, Folder, Upload */}
              <div className="flex items-center gap-2 flex-wrap">
                {/* Search */}
                <div className="relative flex-1 min-w-[120px] max-w-xs">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <MagnifyingGlassIcon className="h-4 w-4 text-gray-400" />
                  </div>
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search..."
                    className="block w-full pl-9 pr-3 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                {/* Folder dropdown */}
                <div className="relative min-w-0 max-w-[140px]">
                  <select
                    value={selectedFolderId || ''}
                    onChange={(e) => setSelectedFolderId(e.target.value || null)}
                    className="appearance-none bg-white border border-gray-300 rounded-md pl-8 pr-6 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 w-full truncate"
                  >
                    <option value="">All Folders</option>
                    {folders.map((folder) => (
                      <option key={folder.id} value={folder.id}>
                        {folder.path}
                      </option>
                    ))}
                  </select>
                  <FolderIcon className="absolute left-2 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                </div>

                {/* Upload button */}
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => setShowUploadModal(true)}
                  className="shrink-0"
                >
                  <ArrowUpTrayIcon className="w-4 h-4 md:mr-1" />
                  <span className="hidden md:inline">Upload</span>
                </Button>
              </div>

              {/* Row 2: Tag chips */}
              {tags.length > 0 && (
                <PickerTagFilter
                  tags={tags}
                  selectedTagIds={selectedTagIds}
                  onChange={setSelectedTagIds}
                />
              )}
            </div>

            {/* Image grid */}
            <div className="flex-1 overflow-y-auto p-4 bg-gray-50 min-h-0">
              {imagesLoading ? (
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
                  {Array.from({ length: 10 }).map((_, i) => (
                    <div key={i} className="aspect-square bg-gray-200 rounded-lg animate-pulse" />
                  ))}
                </div>
              ) : allImages.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 text-gray-500">
                  <PhotoIcon className="w-12 h-12 mb-3 text-gray-300" />
                  <p className="text-sm">No images found</p>
                  <p className="text-xs text-gray-400 mt-1">
                    Try adjusting your filters or upload new images
                  </p>
                </div>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
                  {allImages.map((image) => (
                    <ImagePickerCard
                      key={image.id}
                      image={image}
                      isSelected={localSelectedIds.has(image.id)}
                      onToggle={() => handleToggleImage(image.id)}
                      disabled={isAtMaxSelection && !localSelectedIds.has(image.id)}
                      targetAspectRatio={effectiveTargetRatio}
                    />
                  ))}
                </div>
              )}
            </div>

            {/* Selected images display */}
            <SelectedImagesBar
              selectedImages={selectedImages}
              onRemove={handleRemoveSelected}
              targetAspectRatio={effectiveTargetRatio}
            />

            {/* Footer */}
            <div className="flex items-center justify-between p-4 border-t border-gray-200 bg-white">
              <div className="text-sm text-gray-500">
                {allImages.length} image{allImages.length !== 1 ? 's' : ''} available
                {cropStats.needsCrop > 0 && (
                  <span className="ml-2 text-amber-600">
                    <ScissorsIcon className="w-4 h-4 inline mr-1" />
                    {cropStats.needsCrop} of {cropStats.total} will be cropped
                  </span>
                )}
              </div>
              <div className="flex gap-2">
                <Button variant="secondary" onClick={handleCancel}>
                  Cancel
                </Button>
                <Button onClick={handleConfirm} disabled={localSelectedIds.size === 0}>
                  Confirm Selection ({localSelectedIds.size})
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Upload Modal */}
      <UploadModal
        isOpen={showUploadModal}
        onClose={() => setShowUploadModal(false)}
        folders={folders}
        tags={tags}
        currentFolderId={selectedFolderId}
      />
    </>
  );
}

export default ImagePicker;
